import React, { useState, useEffect } from 'react';
import api from '../services/api';
import streamBlob from '../shared/streamBlob';
import triggerDownload from '../shared/triggerDownload';
import AudioBlobPlayer from './AudioBlobPlayer';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_PREVIEW_LIMIT = 5 * 1024 * 1024; // 5 MB

export default function FilePreviewSection({ node }) {
  const meta = node.value_json || {};
  const filename = meta.filename || node.title || 'file';
  const mimeType = meta.mime_type || 'application/octet-stream';
  const sizeBytes = meta.size_bytes || 0;
  const mimeBase = mimeType.split(';')[0].trim();
  const mimePrefix = mimeBase.split('/')[0];

  const isImage = mimePrefix === 'image';
  const isAudio = mimePrefix === 'audio';
  const isVideo = mimePrefix === 'video';
  const isPDF = mimeBase === 'application/pdf';
  const isText = mimePrefix === 'text' || mimeBase === 'application/json' || mimeBase === 'application/xml';

  const withinLimit = sizeBytes === 0 || sizeBytes <= FILE_PREVIEW_LIMIT;
  const canPreview = withinLimit && (isImage || isAudio || isVideo || isPDF || isText);

  const [state, setState] = useState('idle'); // idle | loading | loaded | error
  const [blobUrl, setBlobUrl] = useState(null);
  const [textData, setTextData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Reset preview when node changes
  const nodeId = node.id;
  useEffect(() => {
    setState('idle');
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setTextData(null);
  }, [nodeId]);

  // Cleanup blob URL on unmount
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  async function loadPreview() {
    setState('loading');
    try {
      if (isText) {
        const data = await api.request(`/nodes/${node.id}/preview`);
        setTextData(data);
        setState('loaded');
      } else {
        const url = await streamBlob(node.id);
        setBlobUrl(url);
        setState('loaded');
      }
    } catch {
      setState('error');
    }
  }

  async function handleDownload() {
    // If the blob is already in memory from preview, reuse it directly
    if (blobUrl) {
      triggerDownload(blobUrl, filename);
      return;
    }
    // Fetch through our authenticated stream endpoint so the browser can
    // trigger a real file download (cross-origin SAS URLs ignore the download attribute)
    setDownloading(true);
    let url = null;
    try {
      url = await streamBlob(node.id);
      triggerDownload(url, filename);
    } catch {
      // last-resort fallback
      window.open(api.getBaseUrl() + `/nodes/${node.id}/stream`, '_blank', 'noopener');
    } finally {
      if (url) URL.revokeObjectURL(url);
      setDownloading(false);
    }
  }

  return (
    <div className="file-preview">
      <div className="file-preview__meta">
        <span className="file-preview__name" title={filename}>{filename}</span>
        <span className="file-preview__badge">{mimeBase}</span>
        {sizeBytes > 0 && <span className="file-preview__size">{formatBytes(sizeBytes)}</span>}
      </div>

      {/* Audio: always load eagerly via AudioBlobPlayer */}
      {isAudio && <AudioBlobPlayer rawFileNodeId={node.id} />}

      {/* Non-audio: lazy load trigger */}
      {!isAudio && state === 'idle' && canPreview && (
        <button className="btn file-preview__load-btn" onClick={loadPreview}>Load preview</button>
      )}
      {!isAudio && state === 'idle' && !canPreview && (
        <div className="file-preview__too-large">
          File too large to preview inline ({formatBytes(sizeBytes)})
        </div>
      )}

      {!isAudio && state === 'loading' && (
        <div className="file-preview__loading">Loading preview…</div>
      )}
      {!isAudio && state === 'error' && (
        <div className="file-preview__error">Preview failed — use Download below</div>
      )}

      {state === 'loaded' && blobUrl && isImage && (
        <img src={blobUrl} alt={filename} className="file-preview__image" />
      )}
      {state === 'loaded' && blobUrl && isVideo && (
        <video controls src={blobUrl} className="file-preview__video" />
      )}
      {state === 'loaded' && blobUrl && isPDF && (
        <iframe src={blobUrl} title={filename} className="file-preview__pdf" />
      )}
      {state === 'loaded' && textData && isText && (
        <pre className="file-preview__text">
          {textData.content}
          {textData.truncated ? '\n… (showing first 50 KB)' : ''}
        </pre>
      )}

      <button className="btn file-preview__download-btn" onClick={handleDownload} disabled={downloading}>
        {downloading ? 'Downloading…' : '↓ Download'}
      </button>
    </div>
  );
}
